import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  Clock,
  Flame,
  Plus,
  Search,
  X,
  Zap,
} from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { useHomeDashboard, PriorityTier, DashboardItem } from '../hooks/useHomeDashboard';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MoveCard } from '../components/ui/MoveCard';
import { CrewAssignmentModal } from '../components/jobs/CrewAssignmentModal';
import { TruckAssignmentModal } from '../components/jobs/TruckAssignmentModal';
import { QuickLogSheet } from '../components/ui/QuickLogSheet';
import { AssignEstimatorSheet } from '../components/ui/AssignEstimatorSheet';
import { NewMoveForm, type NewMoveFormData } from '../components/moves/NewMoveForm';
import { api } from '../lib/api';
import type { Quote, LeadCommunicationType, OrgRole } from '../types/admin';
import type { Job, AssignedCrewMember, AssignedTruck } from '../hooks/useJobs';

// ── Tier Config ─────────────────────────────────────────────────────────────

const TIER_CONFIG: Record<PriorityTier, { label: string; icon: typeof Flame; color: string; bg: string; border: string; description: string }> = {
  1: {
    label: 'Act Now',
    icon: Flame,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    description: 'Actively costing you money or reputation if not handled immediately',
  },
  2: {
    label: 'Soon',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    description: 'Will become urgent in the next 24–48 hours',
  },
  3: {
    label: 'Opportunities',
    icon: Zap,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    description: 'Opportunities to move the pipeline forward',
  },
  4: {
    label: 'On Track',
    icon: Clock,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    description: 'Everything is on track — no action needed',
  },
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function getJobData(item: DashboardItem): Job | null {
  if (item.type !== 'job') return null;
  return item.raw as Job;
}

function getQuoteData(item: DashboardItem): Quote | null {
  return item.raw as Quote;
}

// ── Role-based filtering ────────────────────────────────────────────────────

function filterItemsByRole(
  items: DashboardItem[],
  role: OrgRole,
  userId: string,
): DashboardItem[] {
  return items.filter(item => {
    const q = item.raw as Quote;
    const j = item.raw as Job;

    if (item.type === 'lead') {
      // Leads: admin, dispatcher, and field lead see leads
      return role === 'admin' || role === 'dispatcher' || role === 'field_lead';
    }

    if (item.type === 'quote') {
      if (role === 'estimator') {
        // Estimators: only their assigned quotes
        return q.quoteLeadUserId === userId;
      }
      if (role === 'admin' || role === 'dispatcher' || role === 'field_lead') {
        // Admin/Dispatcher/Field Lead: their quotes OR unassigned quotes
        return q.quoteLeadUserId === userId || !q.quoteLeadUserId;
      }
      return false;
    }

    if (item.type === 'job') {
      const isOnCrew = (j.assignedCrew ?? []).some(
        (c: AssignedCrewMember) => c.userId === userId,
      );
      const crewNeeded = j.logisticsSummary?.crewSize ?? (q.formula?.finalCrewSize) ?? 0;
      const truckNeeded = j.logisticsSummary?.truckQuantity ?? (q.formula?.truckQuantity) ?? 0;
      const crewAssigned = (j.assignedCrew ?? []).length;
      const truckAssigned = (j.assignedTrucks ?? []).length;
      const fullyAssigned = crewNeeded > 0 && truckNeeded > 0
        && crewAssigned >= crewNeeded && truckAssigned >= truckNeeded;

      if (role === 'mover') {
        // Movers: only jobs where they are assigned crew
        return isOnCrew;
      }
      if (role === 'admin' || role === 'dispatcher' || role === 'field_lead') {
        // Admin/Dispatcher/Field Lead: on crew OR not fully assigned
        return isOnCrew || !fullyAssigned;
      }
      return false;
    }

    return false;
  });
}

// ── Search filtering ────────────────────────────────────────────────────────

function searchItems(items: DashboardItem[], query: string): DashboardItem[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase().trim();
  return items.filter(item => {
    const raw = item.raw as Quote & Job;
    const fields = [
      raw.quoteNumber,
      raw.customerCompanyName,
      raw.pointOfContact?.name,
      raw.addresses?.origin?.address,
      raw.addresses?.destination?.address,
    ];
    return fields.some(f => f && f.toLowerCase().includes(q));
  });
}

// ── Re-bucket filtered items into priority tiers ────────────────────────────

function bucketByTier(items: DashboardItem[]): Record<PriorityTier, DashboardItem[]> {
  const result: Record<PriorityTier, DashboardItem[]> = { 1: [], 2: [], 3: [], 4: [] };
  for (const item of items) {
    result[item.tier].push(item);
  }
  return result;
}

// ── Priority Queue View ─────────────────────────────────────────────────────

function PriorityQueueView({
  items,
  onAssignCrew,
  onAssignTruck,
  onQuickLog,
  onAssignEstimator,
}: {
  items: Record<PriorityTier, DashboardItem[]>;
  onAssignCrew: (item: DashboardItem) => void;
  onAssignTruck: (item: DashboardItem) => void;
  onQuickLog: (item: DashboardItem) => void;
  onAssignEstimator: (item: DashboardItem) => void;
}) {
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({ 4: true });

  const toggleCollapse = (tier: number) => {
    setCollapsed(prev => ({ ...prev, [tier]: !prev[tier] }));
  };

  const tiers: PriorityTier[] = [1, 2, 3, 4];

  return (
    <div className="space-y-4">
      {tiers.map(tier => {
        const cfg = TIER_CONFIG[tier];
        const tierItems = items[tier];
        const Icon = cfg.icon;
        const isCollapsed = collapsed[tier] ?? false;

        if (tierItems.length === 0) return null;

        return (
          <div key={tier}>
            <button
              type="button"
              onClick={() => toggleCollapse(tier)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border ${cfg.border} ${cfg.bg}
                         transition-colors hover:opacity-90 mb-2`}
            >
              <Icon className={`w-5 h-5 ${cfg.color} shrink-0`} />
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm ${cfg.color}`}>{cfg.label}</span>
                  <span className="text-xs text-slate">
                    {tierItems.length} item{tierItems.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-[11px] text-slate leading-snug">{cfg.description}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
            </button>

            {!isCollapsed && (
              <div className="space-y-2 mb-4">
                {tierItems.map(item => (
                  <MoveCard
                    key={item.id}
                    data={item.raw}
                    stage={item.type}
                    design="C"
                    statusLabel={item.status}
                    statusClasses={item.statusClasses}
                    reason={item.reason}
                    onAssignCrew={item.type === 'job' ? () => onAssignCrew(item) : undefined}
                    onAssignTruck={item.type === 'job' ? () => onAssignTruck(item) : undefined}
                    onQuickLog={item.type === 'lead' ? () => onQuickLog(item) : undefined}
                    onAssignEstimator={item.type === 'quote' ? () => onAssignEstimator(item) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {tiers.every(t => items[t].length === 0) && (
        <div className="text-center py-12 text-slate">
          <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">All clear</p>
          <p className="text-sm mt-1">No items need your attention right now.</p>
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function HomeDashboardPage() {
  const navigate = useNavigate();
  const { activeOrg, currentUser } = useUserProfile();
  const orgId = activeOrg?.orgId ?? '';
  const userRole = activeOrg?.role ?? 'estimator';
  const userId = currentUser?.uid ?? '';
  const { loading, error, allItems, refetch } = useHomeDashboard(orgId || null);
  const [search, setSearch] = useState('');
  const [showNewSheet, setShowNewSheet] = useState(false);
  // The setup wizard's "Create your first quote" lands here with ?new=1.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setShowNewSheet(true);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Feature flag checks for quote type selector
  const commercialEnabled = activeOrg?.featureCommercialMovingQuotes !== false;
  const residentialEnabled = activeOrg?.featureResidentialMovingQuotes ?? false;
  const bothTypesEnabled = commercialEnabled && residentialEnabled;

  const handleCreateMove = useCallback(async (data: NewMoveFormData) => {
    const res = await api.post<{ success: boolean; data: Quote }>(`/quotes/${orgId}`, data);
    setShowNewSheet(false);
    navigate(`/moves/${res.data.id}`);
    return res.data;
  }, [orgId, navigate]);

  // Modal state
  const [crewAssignItem, setCrewAssignItem] = useState<DashboardItem | null>(null);
  const [truckAssignItem, setTruckAssignItem] = useState<DashboardItem | null>(null);
  const [quickLogItem, setQuickLogItem] = useState<DashboardItem | null>(null);
  const [estimatorAssignItem, setEstimatorAssignItem] = useState<DashboardItem | null>(null);

  // Filter by role, then search, then bucket
  const filteredItems = useMemo(() => {
    const roleFiltered = filterItemsByRole(allItems, userRole, userId);
    const searched = searchItems(roleFiltered, search);
    return bucketByTier(searched);
  }, [allItems, userRole, userId, search]);

  const handleCrewSave = useCallback(async (crew: AssignedCrewMember[]) => {
    if (!crewAssignItem || !orgId) return;
    await api.patch(`/jobs/${orgId}/${crewAssignItem.id}/crew`, { assignedCrew: crew });
    setCrewAssignItem(null);
    refetch();
  }, [crewAssignItem, orgId, refetch]);

  const handleTruckSave = useCallback(async (trucks: AssignedTruck[]) => {
    if (!truckAssignItem || !orgId) return;
    await api.patch(`/jobs/${orgId}/${truckAssignItem.id}/trucks`, { assignedTrucks: trucks });
    setTruckAssignItem(null);
    refetch();
  }, [truckAssignItem, orgId, refetch]);

  const handleQuickLog = useCallback(async (type: LeadCommunicationType, note: string, nextFollowUp: string) => {
    if (!quickLogItem || !orgId) return;
    await api.post(`/quotes/${orgId}/${quickLogItem.id}/communication`, { type, note, nextFollowUp });
    setQuickLogItem(null);
    refetch();
  }, [quickLogItem, orgId, refetch]);

  const handleAssignEstimator = useCallback(async (userId: string) => {
    if (!estimatorAssignItem || !orgId) return;
    await api.patch(`/quotes/${orgId}/${estimatorAssignItem.id}`, { quoteLeadUserId: userId });
    setEstimatorAssignItem(null);
    refetch();
  }, [estimatorAssignItem, orgId, refetch]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const crewJob = crewAssignItem ? getJobData(crewAssignItem) : null;
  const crewQuote = crewAssignItem ? getQuoteData(crewAssignItem) : null;
  const truckJob = truckAssignItem ? getJobData(truckAssignItem) : null;

  return (
    <>
      <div className="max-w-2xl mx-auto px-4 py-4 pb-24 md:pb-8">
        <h1 className="font-display text-h1 text-midnight mb-3">My Moves</h1>

        {/* Search bar */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, quote #, or address…"
            className="w-full h-11 pl-10 pr-10 rounded-xl border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-midnight"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <PriorityQueueView
          items={filteredItems}
          onAssignCrew={setCrewAssignItem}
          onAssignTruck={setTruckAssignItem}
          onQuickLog={setQuickLogItem}
          onAssignEstimator={setEstimatorAssignItem}
        />
      </div>

      {crewAssignItem && crewJob && (
        <CrewAssignmentModal
          orgId={orgId}
          jobId={crewAssignItem.id}
          moveDate={crewJob.movingDate?.preferredDate}
          crewNeeded={crewJob.logisticsSummary?.crewSize ?? crewQuote?.formula?.finalCrewSize ?? 0}
          driverQuantity={crewJob.logisticsSummary?.truckQuantity ?? crewQuote?.formula?.truckQuantity ?? 0}
          quoteType={(crewQuote?.type as 'commercial' | 'residential') ?? 'commercial'}
          currentCrew={crewJob.assignedCrew ?? []}
          onSave={handleCrewSave}
          onClose={() => setCrewAssignItem(null)}
        />
      )}

      {truckAssignItem && truckJob && (
        <TruckAssignmentModal
          orgId={orgId}
          jobId={truckAssignItem.id}
          moveDate={truckJob.movingDate?.preferredDate}
          trucksNeeded={truckJob.logisticsSummary?.truckQuantity ?? (getQuoteData(truckAssignItem))?.formula?.truckQuantity ?? 0}
          currentTrucks={truckJob.assignedTrucks ?? []}
          onSave={handleTruckSave}
          onClose={() => setTruckAssignItem(null)}
        />
      )}

      {quickLogItem && (
        <QuickLogSheet
          leadName={(quickLogItem.raw as Quote).pointOfContact?.name || (quickLogItem.raw as Quote).customerCompanyName || 'Lead'}
          onSubmit={handleQuickLog}
          onClose={() => setQuickLogItem(null)}
        />
      )}

      {estimatorAssignItem && (
        <AssignEstimatorSheet
          orgId={orgId}
          customerName={(estimatorAssignItem.raw as Quote).customerCompanyName || (estimatorAssignItem.raw as Quote).pointOfContact?.name || 'Quote'}
          onAssign={handleAssignEstimator}
          onClose={() => setEstimatorAssignItem(null)}
        />
      )}

      {/* Floating action button — create a new move */}
      {orgId && (
        <button
          type="button"
          onClick={() => setShowNewSheet(true)}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+16px)] right-4
                     z-40 h-14 w-14 rounded-full bg-plum text-white shadow-brand-md
                     flex items-center justify-center
                     hover:bg-rose active:opacity-80
                     transition-all duration-150"
          aria-label="New move"
        >
          <Plus size={26} />
        </button>
      )}

      {showNewSheet && (
        <NewMoveForm
          onClose={() => setShowNewSheet(false)}
          onCreate={handleCreateMove}
          showTypeSelector={bothTypesEnabled}
          defaultType={residentialEnabled && !commercialEnabled ? 'residential' : 'commercial'}
          orgId={orgId}
        />
      )}
    </>
  );
}
